import { useMutation, useQueryClient } from '@tanstack/react-query';
import * as Api from '../api';
import { useAuthContext } from '../context';
import type { IEntity } from '../types';

export const PROFILE_NAME_MAX_LENGTH = 100;

/** Updates only the current user's display name, preserving their access context. */
export function useUpdateProfileName() {
  const { state, methods } = useAuthContext();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (value: string) => {
      const name = value.trim();
      if (!state.user) throw new Error('Authentication required');
      if (!name || name.length > PROFILE_NAME_MAX_LENGTH) throw new Error('Invalid name');
      const userId = state.user.id;
      const profileQuery = queryClient
        .getQueryCache()
        .find({ queryKey: ['auth', 'profile'], exact: true });
      const { data } = await Api.UpdateProfile({ name });
      if (typeof data?.name !== 'string' || !data.name.trim())
        throw new Error('Invalid profile response');
      return { userId, name: data.name, profileQuery };
    },
    onSuccess: ({ userId, name, profileQuery }) => {
      // Logout and new logins remove this query; ordinary navigation retains it.
      const isCurrentSession = () =>
        profileQuery &&
        queryClient.getQueryCache().find({ queryKey: ['auth', 'profile'], exact: true }) ===
          profileQuery;
      if (!isCurrentSession()) return;
      methods.setUser((user) =>
        isCurrentSession() && user?.id === userId ? { ...user, name } : user,
      );
      queryClient.setQueryData<{ user: IEntity.User }>(['auth', 'profile'], (profile) =>
        profile?.user.id === userId ? { ...profile, user: { ...profile.user, name } } : profile,
      );
    },
  });
}
